import { useMemo, useState } from 'react'
import { useAccount, useReadContract, useReadContracts } from 'wagmi'
import { T } from '@/lib/theme'
import { SectionHeader } from '@/components/dashboard/SectionHeader'
import { Button, Panel, SectionLabel, Stat, Badge } from '@/components/ui'
import { ADDRESSES, IS_LIVE } from '@/lib/contracts'
import { ERC20_ABI } from '@/lib/erc20'
import poolAbi from '@/lib/abis/HorizonLendingPool.json'
import { fmtUsd, fmtPct, truncAddr, toNumber, POLL_SLOW, fmtBps } from '@/lib/utils'
import { addressLink } from '@/lib/contracts'
import { useContractTx } from '@/hooks/useContractTx'
import { useAction } from '@/hooks/useAction'
import { useProtocolState } from '@/hooks/useProtocolState'
import { useAllBorrowers } from '@/hooks/useAllBorrowers'
import { computeAccountUsd } from '@/lib/poolMath'

const SCALE = 1_000_000_000_000_000_000n
const MAX_UINT = (1n << 256n) - 1n
const BPS = 10_000n

/// Shared by the per-row LiquidateButton and the LiquidateAllButton batch action - both need the
/// exact same (debtAsset, collateralAsset, maxRepayWei) picked for a given position: the largest
/// debt reserve to repay and the largest collateral reserve to seize (the one-click default; a
/// liquidator wanting a specific pairing can still call liquidationCall directly). WETH is a
/// plain ERC20 reserve like any other now (the pool has no native-ETH concept at all - see
/// HorizonLendingPool.sol's own doc comment), so repaying WETH debt needs a normal approve, same
/// as USDx - no more native-value/refund-buffer branch here.
///
/// **"Largest" means largest in USD, and that is the whole point.** This used to compare raw wei
/// between two hardcoded reserves, which is only ever accidentally right: wei of different tokens
/// are not comparable quantities. Once WBTC was listed the bug became total rather than subtle -
/// WBTC has 8 decimals, so 1 WBTC is 1e8 and 1 USDx is 1e18, and a raw comparison would refuse to
/// seize $65,000 of BTC in favour of one dollar of stablecoin, every single time.
function pickLiquidationArgs(position, d) {
  const usd = (byAsset, r) => toNumber(byAsset[r.asset?.toLowerCase()] ?? 0n, r.decimals) * r.priceUsd
  const largest = (byAsset) => d.reserves.reduce(
    (best, r) => (usd(byAsset, r) > best.value ? { reserve: r, value: usd(byAsset, r) } : best),
    { reserve: null, value: 0 },
  ).reserve

  const debtReserve = largest(position.debts)
  const collateralReserve = largest(position.supplies)
  // No debt or no collateral anywhere means nothing to pair; callers already gate on totalDebtUsd,
  // so this is belt-and-braces rather than an expected path.
  if (!debtReserve || !collateralReserve) return { debtAsset: null, collateralAsset: null, maxRepayWei: 0n }

  const debtWei = position.debts[debtReserve.asset?.toLowerCase()] ?? 0n
  const maxRepayWei = (debtWei * BigInt(d.closeFactorBps)) / BPS
  return { debtAsset: debtReserve.asset, collateralAsset: collateralReserve.asset, maxRepayWei }
}

/// Every open position with debt anywhere in the pool, sorted riskiest-first, with a one-click
/// liquidate trigger on anything eligible - the gap flagged when the money market first shipped:
/// `liquidate()`/`liquidationCall()` had zero UI trigger anywhere, permissionless in name only
/// since nobody could actually see a position worth liquidating without reading the chain
/// directly. Addresses come from `useAllBorrowers` (an event-log scan across every reserve, not
/// on-chain enumeration - see that hook's own doc comment for the known scaling limitation).
///
/// Risk is now account-wide (`healthFactor`/`accountData`), not per-market - a symmetric pool
/// lets one account hold debt and collateral across BOTH reserves at once. The one-click
/// liquidate button picks the account's largest debt reserve and largest collateral reserve as
/// the (debtAsset, collateralAsset) pair to call `liquidationCall` with - the true cross-asset
/// capability the retired same-pair markets couldn't offer at all.
export function LiquidationsSection() {
  const { address: user } = useAccount()
  const { d, refetch } = useProtocolState()
  const { addresses, loading: loadingAddresses } = useAllBorrowers()
  // Debt and collateral across EVERY listed reserve, not the two this page used to hardcode.
  // With WBTC and wstETH listed, reading only WETH and USDx understated a borrower's collateral
  // and debt - the health factor itself still came from the contract and stayed correct, so the
  // sorting was right while the USD figures beside it quietly were not.
  const perBorrower = 1 + d.reserves.length * 2
  const { data: reads, refetch: refetchPositions } = useReadContracts({
    contracts: addresses.flatMap((addr) => ([
      { address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'healthFactor', args: [addr] },
      ...d.reserves.map((r) => ({ address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'debtOf', args: [r.asset, addr] })),
      ...d.reserves.map((r) => ({ address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'supplyBalanceOf', args: [r.asset, addr] })),
    ])),
    query: { enabled: IS_LIVE && addresses.length > 0 && d.reserves.length > 0, refetchInterval: POLL_SLOW },
  })

  const positions = useMemo(() => {
    if (!reads) return []
    return addresses
      .map((addr, i) => {
        const base = i * perBorrower
        const healthFactor = reads[base]?.result
        if (healthFactor == null) return null
        const n = d.reserves.length
        // Keyed by asset ADDRESS rather than by a display key like 'eth'/'usdx' - the address is
        // what liquidationCall actually takes, and it cannot drift the way a display slug can.
        const debts = {}
        const supplies = {}
        d.reserves.forEach((r, j) => {
          const a = r.asset?.toLowerCase()
          debts[a] = reads[base + 1 + j]?.result ?? 0n
          supplies[a] = reads[base + 1 + n + j]?.result ?? 0n
        })
        // HorizonLendingPool's own (removed) `accountData(user)` equivalent - see computeAccountUsd's
        // own doc comment in src/lib/poolMath.js. toNumber carries each reserve's OWN decimals:
        // supplyBalanceOf/debtOf return native decimals, so defaulting to 18 would have read a WBTC
        // balance 10 orders of magnitude small.
        const { totalCollateralUsd, totalDebtUsd } = computeAccountUsd(d.reserves.map((r) => ({
          supplyBalance: toNumber(supplies[r.asset?.toLowerCase()] ?? 0n, r.decimals),
          debt: toNumber(debts[r.asset?.toLowerCase()] ?? 0n, r.decimals),
          priceUsd: r.priceUsd,
          collateralFactorBps: r.collateralFactorBps,
        })))
        return { address: addr, totalCollateralUsd, totalDebtUsd, healthFactor, debts, supplies }
      })
      .filter((p) => p && p.totalDebtUsd > 0) // closed/collateral-only positions aren't liquidation candidates
      .map((p) => {
        // Can the pool actually price ANY of this borrower's collateral? `priceUsd` is 0 exactly
        // when `pool.priceOf` reverted on an unreadable feed - and that is the same condition
        // `PoolInvariants.computeSeize` now refuses on (CHANGELOG.md item 98): with no readable
        // price there is no honest exchange rate, so the seize is rejected rather than guessed.
        //
        // This matters here because an unreadable collateral feed also DROPS that collateral from
        // the health factor, so the position reads as deeply unhealthy at the same moment it
        // becomes unliquidatable. Without this check the row would show "Liquidatable", enable the
        // button, and send a transaction that cannot succeed - `pickLiquidationArgs` returns a null
        // collateralAsset because unpriced reserves never win its largest-by-USD comparison.
        const seizable = d.reserves.some(
          (r) => r.priceUsd > 0 && (p.supplies[r.asset?.toLowerCase()] ?? 0n) > 0n,
        )
        return {
          ...p,
          ltvPct: p.totalCollateralUsd > 0 ? (p.totalDebtUsd / p.totalCollateralUsd) * 100 : 0,
          // Unpriceable is NOT "healthy" - it is a distinct, temporary state the operator needs to
          // see, so it gets its own flag rather than being folded into the healthy branch.
          unpriceable: p.healthFactor < SCALE && !seizable,
          liquidatable: p.healthFactor < SCALE && seizable,
        }
      })
      .sort((a, b) => (a.healthFactor < b.healthFactor ? -1 : a.healthFactor > b.healthFactor ? 1 : 0))
  }, [addresses, reads, d.reserves, perBorrower])

  const liquidatableCount = positions.filter((p) => p.liquidatable).length

  function onTx() {
    refetch().catch((e) => console.error('refetch after tx failed:', e))
    refetchPositions().catch((e) => console.error('refetchPositions after tx failed:', e))
  }

  return (
    <div id="liquidations">
      <SectionHeader
        title="Risk"
        subtitle="Every open position with debt, sorted by risk. Undercollateralized accounts can be liquidated by anyone, at a discount, in any asset."
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
        <div style={statStrip}>
          <Stat size="cell" label="Open positions" value={positions.length} />
          <Stat size="cell" divider label="Liquidatable now" value={liquidatableCount} color={liquidatableCount > 0 ? T.negative : T.positive} />
          <Stat size="cell" divider label="Close factor" value={fmtBps(d.closeFactorBps)} />
        </div>

        <Panel style={{ padding: 0 }}>
          <div style={{ padding: '20px 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <SectionLabel>Positions</SectionLabel>
            <LiquidateAllButton positions={positions} user={user} d={d} onTx={onTx} />
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>Borrower</th>
                  <th style={{ ...th, textAlign: 'right' }}>Collateral</th>
                  <th style={{ ...th, textAlign: 'right' }}>Debt</th>
                  <th style={{ ...th, textAlign: 'right' }}>Health Factor</th>
                  <th style={th}>Status</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {positions.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={emptyCell}>
                      {loadingAddresses ? 'Scanning for positions…' : 'No open borrow positions.'}
                    </td>
                  </tr>
                ) : positions.map((p) => (
                  <PositionRow key={p.address} position={p} user={user} d={d} onTx={onTx} />
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  )
}

function PositionRow({ position, user, d, onTx }) {
  const danger = position.liquidatable
  const atRisk = !danger && position.healthFactor < (SCALE * 13n) / 10n // < 1.3
  const link = addressLink(position.address)
  const hfDisplay = position.healthFactor >= MAX_UINT / 2n ? '∞' : (Number(position.healthFactor) / 1e18).toFixed(2)

  return (
    <tr>
      <td style={td}>
        {link ? (
          <a href={link} target="_blank" rel="noreferrer" style={addrLink} className="mono">{truncAddr(position.address)}</a>
        ) : (
          <span className="mono" style={{ color: T.text }}>{truncAddr(position.address)}</span>
        )}
      </td>
      <td style={{ ...td, textAlign: 'right' }} className="mono">{fmtUsd(position.totalCollateralUsd)}</td>
      <td style={{ ...td, textAlign: 'right' }} className="mono">{fmtUsd(position.totalDebtUsd)}</td>
      <td style={{ ...td, textAlign: 'right' }} className="mono">{hfDisplay}</td>
      <td style={td}>
        <Badge color={danger ? T.negative : position.unpriceable ? T.warning : atRisk ? T.warning : T.positive}>
          {danger ? 'Liquidatable' : position.unpriceable ? 'Oracle stale' : atRisk ? 'At Risk' : 'Healthy'}
        </Badge>
      </td>
      <td style={{ ...td, textAlign: 'right' }}>
        <LiquidateButton position={position} user={user} d={d} onTx={onTx} />
      </td>
    </tr>
  )
}

function LiquidateButton({ position, user, d, onTx }) {
  const action = useAction()
  const { debtAsset, collateralAsset, maxRepayWei } = pickLiquidationArgs(position, d)

  async function liquidate() {
    // MAX_UINT repayAmount = "use the full closeFactorBps-capped amount" (same sentinel every
    // amount-taking pool function uses for "all"). liquidationCall is a plain ERC20 pull now -
    // the pool has no native-ETH concept at all anymore, so there's no msg.value/refund-buffer
    // branch to compute here regardless of which asset the debt is in. The debt-asset approval to
    // the pool (checked against maxRepayWei, this position's real cap) is folded in as a pull, so
    // approve+liquidate is one confirmation on a batch wallet.
    const res = await action.run({
      pulls: [{ token: debtAsset, spender: ADDRESSES.LENDING_POOL, amount: maxRepayWei }],
      calls: [{ address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'liquidationCall', args: [debtAsset, collateralAsset, position.address, MAX_UINT] }],
      successMsg: `Liquidated ${truncAddr(position.address)} at a discount.`,
    })
    if (res) onTx?.()
  }

  // Not actually liquidatable - render the same button shape, disabled, instead of swapping it
  // out for a dash. liquidationCall itself would revert AccountHealthy against this position, so
  // this stays unclickable rather than inviting a doomed-to-fail transaction.
  //
  // `unpriceable` is the other disabled case and deserves its own label: the position IS underwater
  // on paper, but none of its collateral has a readable price, so `computeSeize` would revert
  // CollateralPriceUnavailable. Saying "Oracle stale" tells a liquidator to come back rather than
  // leaving them to guess why a visibly-underwater position will not take their transaction.
  if (position.unpriceable) {
    return <Button disabled variant="danger" style={btnSmall} title="This position has no fresh collateral price, so it cannot be liquidated yet.">Price unavailable</Button>
  }
  if (!position.liquidatable) return <Button disabled variant="danger" style={btnSmall}>Liquidate</Button>
  if (!user) return <Button disabled style={btnSmall}>Connect wallet</Button>
  return (
    <Button variant="danger" onClick={liquidate} disabled={action.pending} style={btnSmall}>
      {action.pending ? 'Working…' : 'Liquidate Max'}
    </Button>
  )
}

/// Fires liquidationCall against every currently-liquidatable position, one transaction at a
/// time (the contract has no batch/multicall entrypoint) - sequential, not parallel, since
/// they're all signed by the same wallet and would otherwise race on nonce ordering anyway.
/// A single upfront approval per debt asset actually needed in this run (USDx and/or WETH - the
/// pool has no native-ETH concept at all anymore, see pickLiquidationArgs' own doc comment)
/// covers every subsequent repay in the run, rather than approving per-position. One tx failing
/// (e.g. someone else liquidates it first, mid-run) doesn't stop the rest - useContractTx's own
/// `run` already swallows and toasts errors per-call instead of throwing.
///
/// This is the one flow that deliberately does NOT use useAction / atomic batching: a keeper sweep
/// must be independent per position, not all-or-nothing. Bundling the whole run into one atomic
/// EIP-5792 batch would revert every liquidation the moment one position got sniped mid-run - the
/// opposite of what a sweep wants. See INTENT-UX-PLAN.md §7 (atomicity limits).
function LiquidateAllButton({ positions, user, d, onTx }) {
  const tx = useContractTx()
  const [progress, setProgress] = useState(null) // { done, total } | null

  const liquidatable = positions.filter((p) => p.liquidatable)
  // Total this run will actually try to repay, per debt asset, across every position that uses
  // it - the real number to check each allowance against, not just "is there any debt in it".
  function neededFor(asset) {
    return liquidatable.reduce((sum, p) => {
      const args = pickLiquidationArgs(p, d)
      return args.debtAsset === asset ? sum + args.maxRepayWei : sum
    }, 0n)
  }
  const usdxNeeded = neededFor(ADDRESSES.USDX)
  const wethNeeded = neededFor(ADDRESSES.WETH)

  const { data: usdxAllowance, refetch: refetchUsdxAllow } = useReadContract({
    address: ADDRESSES.USDX, abi: ERC20_ABI, functionName: 'allowance',
    args: user ? [user, ADDRESSES.LENDING_POOL] : undefined,
    query: { enabled: !!user && usdxNeeded > 0n, refetchInterval: POLL_SLOW },
  })
  const { data: wethAllowance, refetch: refetchWethAllow } = useReadContract({
    address: ADDRESSES.WETH, abi: ERC20_ABI, functionName: 'allowance',
    args: user ? [user, ADDRESSES.LENDING_POOL] : undefined,
    query: { enabled: !!user && wethNeeded > 0n, refetchInterval: POLL_SLOW },
  })

  async function runAll() {
    if (usdxNeeded > 0n && (usdxAllowance ?? 0n) < usdxNeeded) {
      await tx.run({ address: ADDRESSES.USDX, abi: ERC20_ABI, functionName: 'approve',
        args: [ADDRESSES.LENDING_POOL, MAX_UINT] }, { successMsg: 'USDx approved.' })
      await refetchUsdxAllow()
    }
    if (wethNeeded > 0n && (wethAllowance ?? 0n) < wethNeeded) {
      await tx.run({ address: ADDRESSES.WETH, abi: ERC20_ABI, functionName: 'approve',
        args: [ADDRESSES.LENDING_POOL, MAX_UINT] }, { successMsg: 'WETH approved.' })
      await refetchWethAllow()
    }

    setProgress({ done: 0, total: liquidatable.length })
    for (let i = 0; i < liquidatable.length; i++) {
      const position = liquidatable[i]
      const { debtAsset, collateralAsset } = pickLiquidationArgs(position, d)
      await tx.run({ address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'liquidationCall',
        args: [debtAsset, collateralAsset, position.address, MAX_UINT] },
        { successMsg: `Liquidated ${truncAddr(position.address)} at a discount.` })
      setProgress({ done: i + 1, total: liquidatable.length })
    }
    setProgress(null)
    onTx?.()
  }

  const disabled = !user || liquidatable.length === 0 || tx.pending
  return (
    <Button variant="danger" onClick={runAll} disabled={disabled} style={btnSmall}>
      {progress ? `Liquidating ${progress.done}/${progress.total}…`
        : liquidatable.length > 0 ? `Liquidate All (${liquidatable.length})` : 'Liquidate All'}
    </Button>
  )
}

// ─────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────

const statStrip = {
  display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
  borderRadius: T.r.md, border: `1px solid ${T.border}`, background: 'rgba(255,255,255,0.02)', overflow: 'hidden',
}
const table = { width: '100%', borderCollapse: 'collapse', minWidth: 640 }
const th = {
  textAlign: 'left', padding: '12px 24px', color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption,
  fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: `1px solid ${T.border}`,
}
const td = {
  padding: '14px 24px', borderBottom: `1px solid ${T.border}`, color: T.text, fontFamily: T.fontN, fontSize: T.fs.ui,
}
const addrLink = { color: T.accent, textDecoration: 'none' }
const emptyCell = {
  padding: '32px 24px', textAlign: 'center', color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body,
}
const btnSmall = { padding: '8px 14px', fontSize: T.fs.caption }
